<template>
  <div
    class="notification-dropdown flex max-h-[70vh] w-[380px] max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
    role="dialog"
    aria-label="Notifications"
  >
    <!-- Header: segmented tabs + mark all read -->
    <div
      class="flex shrink-0 items-center justify-between gap-2 border-b border-border px-2.5 py-2"
    >
      <div
        class="notification-tabs flex items-center gap-0.5 rounded-lg border border-border bg-surface-muted p-1"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          class="notification-tab whitespace-nowrap rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors"
          :class="
            store.unreadOnly
              ? 'notification-tab--active'
              : 'text-text-muted hover:text-text-secondary'
          "
          :aria-selected="store.unreadOnly"
          data-testid="notifications-tab-unread"
          @click="store.setUnreadOnly(true)"
        >
          Unread<template v-if="store.unreadTotal"> ({{ store.unreadTotal }})</template>
        </button>
        <button
          type="button"
          role="tab"
          class="notification-tab whitespace-nowrap rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors"
          :class="
            !store.unreadOnly
              ? 'notification-tab--active'
              : 'text-text-muted hover:text-text-secondary'
          "
          :aria-selected="!store.unreadOnly"
          data-testid="notifications-tab-all"
          @click="store.setUnreadOnly(false)"
        >
          All
        </button>
      </div>
      <div class="flex items-center gap-0.5">
        <button
          type="button"
          class="whitespace-nowrap rounded px-1.5 py-1 text-[11px] font-medium text-text-secondary transition-colors hover:bg-surface-hover hover:text-text disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          data-testid="notifications-mark-all"
          :disabled="store.unreadTotal === 0"
          @click="store.markAllRead()"
        >
          Mark all read
        </button>
        <button
          type="button"
          class="flex h-6 w-6 items-center justify-center rounded text-text-muted transition-colors hover:bg-surface-hover hover:text-text"
          aria-label="Notification settings"
          title="Notification settings"
          data-testid="notifications-settings"
          @click="emit('settings')"
        >
          <i class="ri-settings-3-line text-sm" aria-hidden="true" />
        </button>
      </div>
    </div>

    <!-- List -->
    <div ref="listRef" class="notification-list min-h-[80px] flex-1 overflow-y-auto">
      <div v-if="store.itemsLoading && !store.itemsLoaded" data-testid="notifications-loading">
        <div v-for="n in 3" :key="n" class="border-b border-border px-4 py-3 last:border-b-0">
          <div class="flex items-center gap-2">
            <BaseSkeleton width="5rem" height="1rem" rounded />
            <BaseSkeleton width="3rem" height="0.75rem" rounded class="ml-auto" />
          </div>
          <BaseSkeleton width="75%" height="0.75rem" rounded class="mt-2.5" />
          <BaseSkeleton width="100%" height="0.625rem" rounded class="mt-2" />
          <BaseSkeleton width="50%" height="0.625rem" rounded class="mt-1.5" />
        </div>
      </div>

      <div
        v-else-if="store.itemsError"
        class="px-6 py-10 text-center"
        data-testid="notifications-error"
      >
        <p class="text-[13px] font-medium text-text">Couldn't load notifications</p>
        <p class="mt-1 text-[11px] text-text-muted">{{ store.itemsError }}</p>
        <button
          type="button"
          class="mt-3 rounded-md border border-border px-3 py-1.5 text-[11px] font-medium text-text-secondary transition-colors hover:bg-surface-hover"
          data-testid="notifications-retry"
          @click="store.loadItems()"
        >
          Retry
        </button>
      </div>

      <div
        v-else-if="store.items.length === 0"
        class="px-6 py-12 text-center"
        data-testid="notifications-empty"
      >
        <p class="text-[13px] font-medium text-text-secondary">
          {{ store.unreadOnly ? "You're all caught up." : 'No notifications yet.' }}
        </p>
        <p class="mt-1 text-[11px] text-text-muted">
          {{
            store.unreadOnly
              ? 'Action needed items will appear here.'
              : 'Notifications about your work will appear here.'
          }}
        </p>
      </div>

      <button
        v-for="item in store.items"
        v-else
        :key="item.id"
        type="button"
        class="notification-item relative block w-full cursor-pointer border-b border-border py-3 pl-4 pr-3.5 text-left transition-colors last:border-b-0"
        :class="item.read_at ? 'notification-item--read' : 'notification-item--unread'"
        :ref="(el) => registerRow(el, item)"
        :data-id="item.id"
        :data-level="item.level"
        @click="emit('open', item)"
        @mouseenter="startDwell(item)"
        @mouseleave="cancelDwell(item.id)"
        @focus="startDwell(item)"
        @blur="cancelDwell(item.id)"
      >
        <Transition name="notification-marker">
          <span
            v-if="!item.read_at"
            class="notification-item-marker absolute inset-y-0 left-0 w-[2px]"
            aria-hidden="true"
          />
        </Transition>
        <span class="flex items-center gap-2">
          <!-- NOTIF-011: an export being prepared wears no level chip — the spinner in the meta line says it -->
          <NotificationLevelBadge
            v-if="view(item).state !== 'preparing'"
            :level="item.level"
            :read="item.read_at !== null"
          />
          <span class="ml-auto whitespace-nowrap text-[10px] text-text-muted tabular-nums">
            {{ formatRelativeTime(view(item).at) }}
          </span>
        </span>
        <span
          class="notification-item-title mt-1.5 line-clamp-2 block text-[13px] leading-snug"
          :class="item.read_at ? 'font-normal text-text-secondary' : 'font-semibold text-text'"
        >
          {{ item.title }}
        </span>
        <span
          v-if="view(item).facts"
          class="notification-item-body mt-0.5 line-clamp-2 block text-[11px] leading-relaxed text-text-muted"
        >
          {{ view(item).facts }}
        </span>
        <span
          class="notification-item-meta mt-1.5 flex items-center gap-1.5 text-[10px] text-text-muted"
        >
          <i
            v-if="view(item).state === 'preparing'"
            class="ri-loader-4-line animate-spin text-[11px]"
            aria-hidden="true"
            data-testid="notification-export-spinner"
          />
          <span class="truncate">{{ view(item).meta }}</span>
          <!-- One file: a button to look at (the row itself is the button and downloads); several: the dropdown (C2) -->
          <ExportDownloadMenu
            v-if="view(item).state === 'ready' && view(item).export"
            :item="item"
            :exp="view(item).export!"
            size="xs"
            inline
            root-class="ml-auto"
          />
        </span>
      </button>
    </div>

    <!-- Footer -->
    <div v-if="hasListPage" class="shrink-0 border-t border-border">
      <button
        type="button"
        class="flex w-full items-center justify-center gap-1.5 px-3 py-2.5 text-[11px] font-medium text-text-secondary transition-colors hover:bg-surface-hover hover:text-text"
        data-testid="notifications-view-all"
        @click="emit('view-all')"
      >
        View all notifications
        <i class="ri-arrow-right-line text-sm" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * NOTIF-003 — the bell's panel (Readdy design, 2026-10-21): unread / all, mark all read, rows
 * with the level chip and the unread marker, loading / error / empty states, "View all" once the
 * notifications page exists. Positioning and open / close live in NotificationBell; this is the
 * content only.
 *
 * Decision 2 (amended): a Heads-up / Info row is read once seen — the cursor rested on it for
 * `SEEN_DWELL_MS`, it has keyboard focus, or (touch) it is in view. Action needed only by click.
 *
 * NOTIF-011 — an export's row (Readdy, 2026-10-29): no chip and a spinner before "Sales ·
 * Sales History" while it is being prepared ("Started 2 min ago"), the facts and a Download
 * hint once ready (the click downloads — the store's rule), "Expired on …" after the window.
 */
import { nextTick, onBeforeUnmount, ref, watch, type ComponentPublicInstance } from 'vue'
import { useRouter } from 'vue-router'
import BaseSkeleton from '@/components/ui/BaseSkeleton.vue'
import ExportDownloadMenu from '@/components/notifications/ExportDownloadMenu.vue'
import NotificationLevelBadge from '@/components/notifications/NotificationLevelBadge.vue'
import { useNotificationsStore, type NotificationItem } from '@/stores/notificationsStore'
import { rowView } from '@/lib/notifications/exports'
import { LEVELS_READ_ON_SEEN, NOTIFICATIONS_PAGE_PATH } from '@/lib/notifications/pages'
import { SEEN_VISIBLE_THRESHOLD, hoverCapable, seenDwellMs } from '@/lib/notifications/seen'
import { formatRelativeTime } from '@/utils/datetimeUtils'

const emit = defineEmits<{ open: [item: NotificationItem]; 'view-all': []; settings: [] }>()

const store = useNotificationsStore()
const router = useRouter()

/** The notifications page is a later slice (NOTIF-007); the footer appears once its route exists. */
const hasListPage = router.getRoutes().some((r) => r.path === NOTIFICATIONS_PAGE_PATH)

/** What the row shows: an export's state, facts and meta line, or the plain row's. */
function view(item: NotificationItem) {
  return rowView(item, formatRelativeTime)
}

// ---------------------------------------------------------------- seen: hover / focus (pointer devices)

function seeable(item: NotificationItem): boolean {
  return item.read_at === null && LEVELS_READ_ON_SEEN.includes(item.level)
}

const dwellTimers = new Map<string, ReturnType<typeof setTimeout>>()

function startDwell(item: NotificationItem) {
  if (!seeable(item)) return
  cancelDwell(item.id)
  dwellTimers.set(
    item.id,
    setTimeout(() => {
      dwellTimers.delete(item.id)
      void store.markSeen(item)
    }, seenDwellMs())
  )
}

function cancelDwell(id: string) {
  const timer = dwellTimers.get(id)
  if (timer !== undefined) {
    clearTimeout(timer)
    dwellTimers.delete(id)
  }
}

// ---------------------------------------------------------------- seen: in view (touch devices)

const listRef = ref<HTMLElement | null>(null)
const rowEls = new Map<string, HTMLElement>()
let observer: IntersectionObserver | null = null
const observeInView = !hoverCapable() && typeof IntersectionObserver === 'function'

function registerRow(el: Element | ComponentPublicInstance | null, item: NotificationItem) {
  if (el instanceof HTMLElement) rowEls.set(item.id, el)
  else rowEls.delete(item.id)
}

function onInView(entries: IntersectionObserverEntry[]) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue
    const id = (entry.target as HTMLElement).dataset.id
    const item = store.items.find((i) => i.id === id)
    if (item) void store.markSeen(item)
  }
}

/** (Re)observe the unread seen-able rows; a row that became read drops out. */
function syncInView() {
  if (!observeInView || !listRef.value) return
  observer ??= new IntersectionObserver(onInView, {
    root: listRef.value,
    threshold: SEEN_VISIBLE_THRESHOLD
  })
  observer.disconnect()
  for (const item of store.items) {
    const el = rowEls.get(item.id)
    if (el && seeable(item)) observer.observe(el)
  }
}

watch(
  () => store.items,
  () => void nextTick(syncInView),
  { immediate: true }
)

onBeforeUnmount(() => {
  for (const timer of dwellTimers.values()) clearTimeout(timer)
  dwellTimers.clear()
  observer?.disconnect()
  observer = null
})
</script>

<style scoped>
.notification-tab--active {
  background-color: var(--color-surface-hover);
  color: var(--color-text);
}

.notification-item:hover,
.notification-item:focus-visible {
  background-color: color-mix(in oklab, var(--color-surface-hover) 60%, transparent);
  outline: none;
}

.notification-item-marker {
  background-color: color-mix(in oklab, var(--color-primary) 60%, transparent);
}

/* The unread bar fades out when the row is seen. */
.notification-marker-leave-active {
  transition: opacity 300ms ease-out;
}

.notification-marker-leave-to {
  opacity: 0;
}
</style>
